import primaryComponents from "../../primaryComponents";
import Icon from "../../../assets/icons";
import { useTable } from "react-table";
import { useMemo } from "react";
import secondaryComponents from "..";
import { formatCurrency } from "../../../helpers";
import { v4 as uuidv4 } from "uuid";
import { useSelector } from "react-redux";
import styles from "./BulkRestockTable.module.scss";
import { DeleteIcon } from "../../../assets/svgIcons";
import RadioButtonV2 from "../../primaryComponents/RadioButtonV2";

export const useBulkRestockTable = ({
  restockData,
  setRestockData,
  currentStore,
  control,
  trigger,
  errors,
  isDisabled,
}) => {
  const { storeList } = useSelector((state) => state.profileDetailsReducer);

  const deleteRow = (index) => {
    const newData = [...instance?.data];
    newData.splice(index, 1);
    setRestockData(newData);
  };
  const columns = useMemo(() => {
    const headers = [
      {
        Header: "Product Name",
        accessor: "name",
        Cell: ({ cell: { value }, row }) => (
          <span className="d-flex align-items-center">
            {!isDisabled ? (
              <span
                className="mr-2 cursor-pointer"
                onClick={() => deleteRow(row?.index)}
              >
                <DeleteIcon />
              </span>
            ) : null}
            <secondaryComponents.ConcatenateWithTooltip
              value={value || row?.original?.product_name}
              noOfCharacters={200}
            />
          </span>
        ),
      },
      {
        Header: "Store Name",
        accessor: "store_name",
        Cell: ({ cell: { value } }) => value,
      },
      {
        Header: "Quantity",
        accessor: "quantity",
        Cell: ({ cell: { value }, row }) => (
          <span className="d-flex align-items-center">
            {/* Input Field with Fixed Width */}
            <primaryComponents.InputFieldV2
              name={`restockProperties.${row?.index}.quantity`}
              control={control}
              classNames={`white ${styles.inputField}`}
              isRequired
              type="number"
              step={0.01}
              min={0.01}
              value={value}
              isDisabled={isDisabled}
              errorMessage={errors?.restockProperties?.[row?.index]?.quantity}
              onKeyUp={() => {
                errors?.restockProperties?.[row?.index]?.quantity !==
                  undefined &&
                  trigger(`restockProperties.${row?.index}.quantity`);
              }}
              onChange={({ target: { value } }) => {
                const newData = [...instance?.data];
                newData[row?.index].quantity = value;
                setRestockData(newData);
              }}
            />

            {/* Label with Fixed Width */}
            <span className="mb-3 ml-1">{row?.original?.unit}</span>
          </span>
        ),
      },
      {
        Header: "Cost Price",
        accessor: "cost_price",
        Cell: ({ cell: { value }, row }) => (
          <primaryComponents.AmountFieldV2
            control={control}
            name={`restockProperties.${row?.index}.cost_price`}
            currency_code={
              storeList?.find(
                (storeItem) =>
                  `${storeItem?.id}` === `${row?.original?.store_id}`,
              )?.currency_code
            }
            value={value}
            isDisabled={isDisabled}
            classNames={`white ${styles.inputField}`}
            onChange={({ target: { value } }) => {
              const newData = [...instance?.data];
              newData[row?.index].cost_price = value;
              setRestockData(newData);
            }}
          />
        ),
      },
      {
        Header: "Selling Price",
        accessor: "pricings",
        Cell: ({ cell: { value: pricings }, row }) => (
          <primaryComponents.AmountFieldV2
            control={control}
            name={`restockProperties.${row?.index}.selling_price`}
            currency_code={
              storeList?.find(
                (storeItem) =>
                  `${storeItem?.id}` === `${row?.original?.store_id}`,
              )?.currency_code
            }
            isDisabled={isDisabled}
            value={pricings?.[0]?.price || row?.original?.selling_price || ""}
            classNames={`white ${styles.inputField}`}
            onChange={({ target: { value } }) => {
              const newData = [...instance?.data];
              newData[row?.index].pricings[0].price = value;
              setRestockData(newData);
            }}
          />
        ),
      },
      {
        Header: "Expiry Date",
        accessor: "expiry_date",
        Cell: ({ cell: { value }, row }) => (
          <primaryComponents.DateInputV2
            control={control}
            isDisabled={isDisabled}
            classNames={`white ${styles.inputField}`}
            name={`restockProperties.${row?.index}.expiry_date`}
            value={value || ""}
            onChange={({ target: { value } }) => {
              const newData = [...instance?.data];
              newData[row?.index].expiry_date = value;
              setRestockData(newData);
            }}
          />
        ),
      },
      !isDisabled
        ? {
            Header: "Use prices across other stores?",
            accessor: "apply_pricing_to_all_stores",
            Cell: ({ row }) => {
              const uuid = uuidv4();

              const handleRadioChange = (value) => {
                const newData = [...instance?.data];
                newData[row.index].apply_pricing_to_all_stores = value;
                setRestockData(newData);
              };

              return (
                <div
                  className="d-flex mb-3 justify-content-center align-items-center py-2"
                  style={{
                    gap: "4px",
                    background: "#fff",
                    borderRadius: "16px",
                    minHeight: "15px",
                    maxWidth: "140px",
                  }}
                >
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "12px" }}
                  >
                    <RadioButtonV2
                      name={`yesNo-BulkRestockData${uuid}`}
                      id={`yes-${row.index}`}
                      value={true}
                      checked={row.original?.apply_pricing_to_all_stores}
                      onChange={handleRadioChange}
                      label="Yes"
                      isDisabled={isDisabled}
                    />

                    <div
                      style={{
                        width: "2px",
                        height: "20px",
                        backgroundColor: "#ccc",
                      }}
                    ></div>

                    {/* No Radio */}
                    <RadioButtonV2
                      name={`yesNo-BulkRestockData${uuid}`}
                      id={`no-${row.index}`}
                      value={false}
                      checked={!row.original?.apply_pricing_to_all_stores}
                      onChange={handleRadioChange}
                      label="No"
                      isDisabled={isDisabled}
                    />
                  </div>
                </div>
              );
            },
          }
        : null,
      {
        Header: <>VAT Rate</>,
        accessor: "vat_rate",
        Cell: ({ cell: { value }, row }) => (
          <primaryComponents.VatPercentField
            name={`restockProperties.${row?.index}.vat_rate`}
            control={control}
            value={value}
            onChange={({ percentValue }) => {
              const newData = [...instance?.data];
              newData[row.index].vat_rate =
                percentValue === "" ? 0 : percentValue;

              setRestockData(newData);
            }}
            onKeyUp={() =>
              errors?.restockProperties?.[row?.index]?.vat_rate &&
              trigger(`restockProperties.${row?.index}.vat_rate`)
            }
            type="number"
            min="0"
            max="100"
            step="0.01"
            errorMessage={errors?.restockProperties?.[row?.index]?.vat_rate}
            dividedValue={
              (row?.original?.cost_price || 0) *
              instance?.data[row?.index].quantity
            }
          />
        ),
      },
      {
        Header: "Discount Rate",
        accessor: "",
        Cell: ({ row }) => (
          <primaryComponents.PercentFieldV2
            control={control}
            name={`restockProperties.${row?.index}.discount_rate`}
            value={
              row?.original?.pricings?.[0]?.discount_rate ||
              row?.original?.discount_rate ||
              ""
            }
            classNames="white"
            onChange={({ percentValue }) => {
              const newData = [...instance?.data];
              newData[row?.index].pricings[0].discount_rate = percentValue;
              setRestockData(newData);
            }}
            dividedValue={
              row?.original?.pricings?.[0]?.price === ""
                ? 0
                : row?.original?.pricings?.[0]?.price ||
                  row?.original?.cost_price ||
                  ""
            }
          />
        ),
      },
      !isDisabled
        ? {
            Header: "Profit Per Unit",
            accessor: "profit",
            Cell: ({ row }) => (
              <primaryComponents.InputField
                classNames={`white ${styles.inputField}`}
                isDisabled={true}
                value={formatCurrency({
                  value:
                    (
                      row?.original?.pricings?.[0]?.price -
                      (row?.original?.pricings?.[0]?.price *
                        row?.original?.pricings?.[0]?.discount_rate) /
                        100
                    ).toFixed(2) - row?.original?.cost_price,
                  currencyCode: storeList?.find(
                    (storeItem) => `${storeItem?.id}` === `${currentStore}`,
                  )?.currency_code,
                })}
              />
            ),
          }
        : null,
      !isDisabled
        ? {
            Header: "Tracking Number",
            accessor: "tracking_number",
            Cell: ({ cell: { value }, row }) => (
              <primaryComponents.InputFieldV2
                control={control}
                name={`restockProperties.${row?.index}.tracking_number`}
                classNames={`white ${styles.inputField}`}
                value={value ?? row?.original?.tracking_number ?? ""}
                onChange={({ target: { value } }) => {
                  const newData = [...instance?.data];
                  newData[row?.index].tracking_number = value;
                  setRestockData(newData);
                }}
              />
            ),
          }
        : null,
      !isDisabled
        ? {
            Header: "Barcode",
            accessor: "barcode",
            Cell: ({ cell: { value }, row }) => (
              <primaryComponents.InputFieldV2
                control={control}
                name={`restockProperties.${row?.index}.barcode`}
                inputIcon={Icon.barCodeIcon}
                classNames={`white ${styles.inputField}`}
                value={value ?? row?.original?.barcode ?? ""}
                onChange={({ target: { value } }) => {
                  const newData = [...instance?.data];
                  newData[row?.index].barcode = value;
                  setRestockData(newData);
                }}
              />
            ),
          }
        : null,
      !isDisabled
        ? {
            Header: "Pricing Type",
            accessor: "",
            Cell: ({ row }) => (
              <primaryComponents.InputField
                value={row?.original?.pricings?.[0]?.pricing_type?.label}
                isDisabled={true}
                classNames={`white ${styles.inputField}`}
              />
            ),
          }
        : null,
      !isDisabled
        ? {
            Header: "Discounted Selling Price",
            accessor: "",
            Cell: ({ row }) => (
              <primaryComponents.InputField
                isDisabled={true}
                value={(() => {
                  let sellingPrice =
                    row?.original?.pricings?.[0]?.price === ""
                      ? 0
                      : row?.original?.pricings?.[0]?.price;
                  let discountPercent =
                    row?.original?.pricings?.[0]?.discount_rate === ""
                      ? 0
                      : row?.original?.pricings?.[0]?.discount_rate;
                  let discount_rate = (sellingPrice * discountPercent) / 100;

                  let discountedSellingPrice = sellingPrice - discount_rate;

                  return formatCurrency({
                    value: discountedSellingPrice,
                    currencyCode: storeList?.find(
                      (storeItem) => `${storeItem?.id}` === `${currentStore}`,
                    )?.currency_code,
                  });
                })()}
                classNames={`white ${styles.inputField}`}
              />
            ),
          }
        : null,
    ].filter(Boolean);

    return headers;
  }, [currentStore, errors]);

  const instance = useTable({
    columns,
    data: restockData,
    autoResetPage: false,
  });

  return { instance };
};
